import { RefObject, useEffect } from 'react';

const FOCUSABLE_SELECTOR = [
    'input:not([disabled])',
    'textarea:not([disabled])',
    'select:not([disabled])',
    'button:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

function isReachable(element: HTMLElement): boolean {
    return !element.closest('[aria-hidden="true"], [inert]');
}

/**
 * Traps Tab navigation inside `containerRef` while `active` is true.
 *
 * Cycles focus between the first and last reachable focusable elements (skipping any
 * descendant inside an `aria-hidden="true"` or `inert` subtree), so modal-like UIs
 * keep the keyboard inside the overlay.
 */
export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, active: boolean): void {
    useEffect(() => {
        if (!active) return;
        const container = containerRef.current;
        if (!container) return;

        const handler = (event: KeyboardEvent) => {
            if (event.key !== 'Tab') return;

            const focusables = Array.from(
                container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
            ).filter(isReachable);
            if (focusables.length === 0) return;

            const [first] = focusables;
            const last = focusables[focusables.length - 1];

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        container.addEventListener('keydown', handler);
        return () => container.removeEventListener('keydown', handler);
    }, [containerRef, active]);
}
